import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = async () => {
    try {
      const response = await api.get(`/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Blog post not found</p>
          <Button asChild>
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="blog-post-page">
      <SEO
        title={post.title}
        description={post.excerpt || (post.content ? post.content.slice(0, 160) : undefined)}
        image={post.image_url}
      />
      <StructuredData schema={[
        schemas.article({
          title: post.title,
          excerpt: post.excerpt || (post.content ? post.content.slice(0, 200) : ''),
          image: post.image_url,
          author: post.author,
          published_at: post.created_at,
          updated_at: post.updated_at,
          slug: post.slug || post.id,
        }),
        schemas.breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title, url: `/blog/${post.slug || post.id}` },
        ]),
      ]} />
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${post.image_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80'})`,
          }}
        ></div>
      </section>

      {/* Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="mb-6" data-testid="back-to-blog-btn">
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </Button>

          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Calendar className="h-4 w-4 mr-2" />
            {new Date(post.created_at).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            <span className="mx-3">•</span>
            <span className="text-[#BCA480]">{post.category.replace('_', ' ')}</span>
          </div>

          <h1 className="text-5xl font-bold mb-6" data-testid="post-title">{post.title}</h1>
          
          <div className="flex items-center mb-8 pb-8 border-b">
            <div>
              <p className="text-gray-600">By <span className="font-semibold text-gray-900">{post.author}</span></p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none" data-testid="post-content">
            {post.content.split('\n').map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>

      {/* Related CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Have Questions?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Our team is here to help with all your real estate needs.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#BCA480] hover:bg-[#a89470]">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/blog">Read More Articles</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
